(function () {
  'use strict';

  var TIER1_MIN = 3500;
  var SHIPPING_TARGET = 7000;
  var TIER2_TARGET = 15000;
  var MAX_DISCOUNT = 0.2;

  var TIER1_GROUPS = ['shiny-t-shirt'];
  var TIER2_GROUPS = [
    'pantalon',
    'shiny-longsleeve',
    'knit-polo',
    'reversible-gilet',
    'tech-set',
    'hooded-tech-set',
    'reversible-jacket',
  ];

  var groupsCache = null;
  var catalogCache = null;
  var productCache = {};
  var moneyFormat = '€{{amount}}';
  var currencyCode = 'EUR';
  var discountCodes = {};

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function resizeImage(src, size) {
    if (!src) return '';
    return src.split('?')[0] + '?width=' + size + '&height=' + size + '&crop=center';
  }

  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, moneyFormat);
    }
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
      }).format(cents / 100);
    } catch (e) {
      return '€' + amount.replace('.', ',');
    }
  }

  function calcEffectivePrice(variantPrice, gap) {
    if (variantPrice <= gap) {
      return { effective: variantPrice, discountPct: 0 };
    }

    var requiredPct = (variantPrice - gap) / variantPrice;

    if (requiredPct <= MAX_DISCOUNT) {
      return { effective: gap, discountPct: requiredPct };
    }

    return {
      effective: Math.round(variantPrice * (1 - MAX_DISCOUNT)),
      discountPct: MAX_DISCOUNT,
    };
  }

  function nearestDiscountCodeKey(discountPct) {
    var pct = Math.round(discountPct * 100);
    if (pct <= 0) return null;
    if (pct <= 5) return '5';
    if (pct <= 10) return '10';
    if (pct <= 15) return '15';
    return '20';
  }

  function loadJson(url) {
    return fetch(url)
      .then(function (r) {
        return r.json();
      })
      .catch(function () {
        return null;
      });
  }

  function loadCatalog(url) {
    if (catalogCache) return Promise.resolve(catalogCache);
    return loadJson(url).then(function (data) {
      catalogCache = data || {};
      if (catalogCache.tier1Groups) TIER1_GROUPS = catalogCache.tier1Groups;
      if (catalogCache.tier2Groups) TIER2_GROUPS = catalogCache.tier2Groups;
      if (catalogCache.discountCodes) discountCodes = catalogCache.discountCodes;
      return catalogCache;
    });
  }

  function loadGroups(url) {
    if (groupsCache) return Promise.resolve(groupsCache);
    return loadJson(url).then(function (data) {
      groupsCache = data;
      return groupsCache;
    });
  }

  function handlesForGroups(groups, groupKeys) {
    var handles = [];
    groupKeys.forEach(function (key) {
      if (!groups[key]) return;
      Object.keys(groups[key].products).forEach(function (handle) {
        if (handles.indexOf(handle) === -1) handles.push(handle);
      });
    });
    return handles;
  }

  function fetchProduct(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);

    return fetch('/products/' + handle + '.js')
      .then(function (r) {
        if (!r.ok) throw new Error('missing');
        return r.json();
      })
      .then(function (product) {
        productCache[handle] = product;
        return product;
      })
      .catch(function () {
        return null;
      });
  }

  function loadProducts(handles) {
    return Promise.all(handles.map(fetchProduct)).then(function (products) {
      return products.filter(Boolean);
    });
  }

  function flattenVariants(products) {
    var variants = [];
    products.forEach(function (product) {
      product.variants.forEach(function (variant) {
        variants.push({
          id: variant.id,
          price: variant.price,
          available: variant.available,
          title: variant.title,
          productTitle: product.title,
          productHandle: product.handle,
          productUrl: product.url,
          image: variant.featured_image
            ? variant.featured_image.src
            : product.featured_image
              ? product.featured_image
              : product.images[0] || '',
        });
      });
    });
    return variants;
  }

  function cartVariantIds(cart) {
    return cart.items.map(function (item) {
      return item.variant_id;
    });
  }

  function cartProductHandles(cart) {
    return cart.items.map(function (item) {
      return item.handle;
    });
  }

  function pickTier1(variants, cartSubtotal, variantIds) {
    var gap = SHIPPING_TARGET - cartSubtotal;
    var available = variants.filter(function (v) {
      return v.available && variantIds.indexOf(v.id) === -1;
    });

    var best = null;
    var bestScore = Infinity;

    available.forEach(function (variant) {
      var pricing = calcEffectivePrice(variant.price, gap);
      var newTotal = cartSubtotal + pricing.effective;
      var unlocks = newTotal >= SHIPPING_TARGET;
      var score = 0;

      if (!unlocks) score += 1000000;
      score += Math.abs(pricing.effective - gap) * 12;
      score += pricing.discountPct * 8000;
      score += Math.max(0, newTotal - SHIPPING_TARGET) * 2;

      if (score < bestScore) {
        bestScore = score;
        best = {
          variant: variant,
          effective: pricing.effective,
          discountPct: pricing.discountPct,
        };
      }
    });

    return best;
  }

  function pickTier2(variants, cartSubtotal, variantIds) {
    var gap = TIER2_TARGET - cartSubtotal;
    if (gap <= 0) return null;

    var available = variants.filter(function (v) {
      return v.available && variantIds.indexOf(v.id) === -1;
    });

    var best = null;
    var bestScore = Infinity;

    available.forEach(function (variant) {
      var score = Math.abs(variant.price - gap);
      if (variant.price < gap) score += 6000;
      score -= variant.price * 0.001;

      if (score < bestScore) {
        bestScore = score;
        best = {
          variant: variant,
          effective: variant.price,
          discountPct: 0,
        };
      }
    });

    return best;
  }

  function renderPrice(original, effective, discountPct) {
    if (discountPct <= 0 || effective >= original) {
      return '<span class="marbenio-curation__price-current">' + formatMoney(original) + '</span>';
    }

    return (
      '<span class="marbenio-curation__price-compare">' +
      formatMoney(original) +
      '</span>' +
      '<span class="marbenio-curation__price-current">' +
      formatMoney(effective) +
      '</span>'
    );
  }

  function renderOffer(offer, tier) {
    var v = offer.variant;
    var btnLabel = tier === 1 ? 'Add to Curation' : 'Accept Private Invitation';
    var imgSrc = v.image ? resizeImage(v.image, 100) : '';

    return (
      '<div class="marbenio-curation__offer">' +
      '<a href="' +
      escapeHtml(v.productUrl) +
      '" class="marbenio-curation__media" tabindex="-1" aria-hidden="true">' +
      (imgSrc
        ? '<img src="' + escapeHtml(imgSrc) + '" alt="" width="50" height="50" loading="lazy" decoding="async">'
        : '') +
      '</a>' +
      '<div class="marbenio-curation__details">' +
      '<a href="' +
      escapeHtml(v.productUrl) +
      '" class="marbenio-curation__title">' +
      escapeHtml(v.productTitle) +
      '</a>' +
      '<div class="marbenio-curation__price">' +
      renderPrice(v.price, offer.effective, offer.discountPct) +
      '</div>' +
      '</div>' +
      '<button type="button" class="marbenio-curation__cta" data-variant-id="' +
      v.id +
      '" data-discount-pct="' +
      offer.discountPct +
      '" data-effective="' +
      offer.effective +
      '">' +
      '<span>' +
      btnLabel +
      '</span>' +
      '</button>' +
      '</div>'
    );
  }

  function renderTier1(offer) {
    return (
      '<div class="marbenio-curation marbenio-curation--tier1">' +
      '<p class="marbenio-curation__lead">A considered addition unlocks Complimentary Priority Shipping.</p>' +
      renderOffer(offer, 1) +
      '</div>'
    );
  }

  function renderTier2(offer) {
    if (!offer) {
      return (
        '<div class="marbenio-curation marbenio-curation--tier2 marbenio-curation--complete">' +
        '<p class="marbenio-curation__lead">Private early access has been reserved for this order.</p>' +
        '<p class="marbenio-curation__sub">Thank you for curating with Marbenio.</p>' +
        '</div>'
      );
    }

    return (
      '<div class="marbenio-curation marbenio-curation--tier2">' +
      '<p class="marbenio-curation__lead">As a valued guest, you have unlocked Complimentary Priority Shipping.</p>' +
      '<p class="marbenio-curation__sub">Reach 150&nbsp;&euro; to unlock private early access to our upcoming limited edition drop.</p>' +
      renderOffer(offer, 2) +
      '</div>'
    );
  }

  function bindCta(root) {
    var btn = root.querySelector('.marbenio-curation__cta');
    if (!btn || btn.dataset.bound) return;
    btn.dataset.bound = '1';
    btn.addEventListener('click', function () {
      onAdd(btn);
    });
  }

  function applyDiscountCode(discountPct) {
    var key = nearestDiscountCodeKey(discountPct);
    if (!key || !discountCodes[key]) return Promise.resolve();

    return fetch(window.routes.cart_update_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ discount: discountCodes[key] }),
    }).catch(function () {});
  }

  function onAdd(btn) {
    if (btn.disabled || btn.classList.contains('loading')) return;

    var variantId = btn.dataset.variantId;
    var discountPct = parseFloat(btn.dataset.discountPct || '0');
    var effective = parseInt(btn.dataset.effective || '0', 10);
    if (!variantId) return;

    var cartDrawer = document.querySelector('cart-drawer');
    btn.classList.add('loading');
    btn.setAttribute('aria-disabled', 'true');

    var config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.method = 'POST';

    var body = {
      items: [
        {
          id: parseInt(variantId, 10),
          quantity: 1,
          properties: {
            _marbenio_curation: 'true',
            _marbenio_effective_cents: String(effective),
            _marbenio_discount_pct: String(Math.round(discountPct * 100)),
          },
        },
      ],
    };

    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      body.sections = cartDrawer.getSectionsToRender().map(function (s) {
        return s.id;
      });
      body.sections_url = window.location.pathname;
    }

    config.body = JSON.stringify(body);

    fetch(window.routes.cart_add_url, config)
      .then(function (r) {
        return r.json();
      })
      .then(function (response) {
        if (response.status) {
          btn.classList.remove('loading');
          btn.removeAttribute('aria-disabled');
          return;
        }

        if (discountPct > 0) {
          return applyDiscountCode(discountPct).then(function () {
            return response;
          });
        }
        return response;
      })
      .then(function (response) {
        if (!response || response.status) return;

        if (typeof publish !== 'undefined' && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'marbenio-cart-curation',
            cartData: response,
          });
        }

        if (cartDrawer && response.sections) {
          cartDrawer.renderContents(response);
          return;
        }

        refreshAll();
      })
      .catch(function () {
        btn.classList.remove('loading');
        btn.removeAttribute('aria-disabled');
      });
  }

  function resolveCuration(el) {
    var groupsUrl = el.dataset.groupsUrl;
    var catalogUrl = el.dataset.catalogUrl;

    return Promise.all([
      loadGroups(groupsUrl),
      loadCatalog(catalogUrl),
      fetch('/cart.js').then(function (r) {
        return r.json();
      }),
    ]).then(function (results) {
      var groups = results[0];
      var cart = results[2];

      if (!groups || !cart.items.length) {
        el.innerHTML = '';
        return;
      }

      var subtotal = cart.items_subtotal_price;
      var variantIds = cartVariantIds(cart);
      var tier = 0;

      if (subtotal >= SHIPPING_TARGET) tier = 2;
      else if (subtotal >= TIER1_MIN && subtotal < SHIPPING_TARGET) tier = 1;
      else {
        el.innerHTML = '';
        return;
      }

      var groupKeys = tier === 1 ? TIER1_GROUPS : TIER2_GROUPS;
      var handles = handlesForGroups(groups, groupKeys).filter(function (handle) {
        return cartProductHandles(cart).indexOf(handle) === -1;
      });

      if (!handles.length) {
        if (tier === 2) {
          el.innerHTML =
            subtotal >= TIER2_TARGET
              ? renderTier2(null)
              : '<div class="marbenio-curation marbenio-curation--tier2">' +
                '<p class="marbenio-curation__lead">As a valued guest, you have unlocked Complimentary Priority Shipping.</p>' +
                '<p class="marbenio-curation__sub">Reach 150&nbsp;&euro; to unlock private early access to our upcoming limited edition drop.</p>' +
                '</div>';
        } else {
          el.innerHTML = '';
        }
        return;
      }

      return loadProducts(handles).then(function (products) {
        var variants = flattenVariants(products);
        var offer = tier === 1 ? pickTier1(variants, subtotal, variantIds) : pickTier2(variants, subtotal, variantIds);

        if (tier === 1) {
          el.innerHTML = offer ? renderTier1(offer) : '';
        } else {
          el.innerHTML = renderTier2(offer);
        }

        if (offer) bindCta(el);
      });
    });
  }

  function refreshAll() {
    document.querySelectorAll('marbenio-cart-curation').forEach(function (el) {
      resolveCuration(el);
    });
  }

  if (!customElements.get('marbenio-cart-curation')) {
    customElements.define(
      'marbenio-cart-curation',
      class MarbenioCartCuration extends HTMLElement {
        connectedCallback() {
          moneyFormat = this.dataset.moneyFormat || moneyFormat;
          currencyCode = this.dataset.currency || currencyCode;
          resolveCuration(this);
        }
      }
    );
  }

  if (typeof subscribe !== 'undefined' && window.PUB_SUB_EVENTS) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      setTimeout(refreshAll, 150);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    var observer = new MutationObserver(function () {
      if (drawer.classList.contains('active')) refreshAll();
    });

    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  });
})();
